'use client'

import { useEffect } from 'react'

import {
	getCheapestAllInsurancePrice,
	getSelfPaidInsurancePrice,
	isSelfPaidInsuranceCode
} from '@/_lib/order/template-visit-pricing'
import { AMBULANCE_PRICE_LIST_TABLE_TYPE } from '@/_types/ambulance'
import {
	calculateDoctorAmbulancePrice,
	getTemplatePriceBeforeAnnualProgram
} from '@/app/[locale]/order/_utils/price-calculation.utils'

import {
	CartProduct,
	CartProductType,
	type TemplateCartPricingSnapshot,
	useOrderFlow
} from '../../../../_context/order-flow-context'

import type { ApiResponse, IOrderTimeSlotDetail } from '@/_types'
import type { IAnnualProgramDiscountData } from '@/app/[locale]/order/_types/annual-program-discount'

type UseTemplateCartPricingSyncParams = {
	primaryProduct: CartProduct | undefined
	primaryType: CartProductType | undefined
	timeslotDetail: ApiResponse<IOrderTimeSlotDetail> | undefined
	showInsurance: boolean
	watchedInsurance: string | undefined
	annualProgramDiscount: IAnnualProgramDiscountData | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Looks up the insurance-company code for a given insurer ID string
 * by scanning the template's `insuranceCompanies` list.
 * Returns `null` when the entry is not found (e.g. data not yet loaded).
 */
function findInsuranceCodeById(
	insuranceCompanies:
		| { insuranceCompany: { id?: number; code?: string | null } }[]
		| null
		| undefined,
	insuranceId: string | null | undefined
): string | null {
	if (!insuranceId || !insuranceCompanies?.length) return null
	const entry = insuranceCompanies.find(ic => ic.insuranceCompany?.id?.toString() === insuranceId)
	return entry?.insuranceCompany?.code ?? null
}

// ---------------------------------------------------------------------------
// Price-label decision
// ---------------------------------------------------------------------------

/**
 * Determines the cart row label and "od" (from) prefix flag for a template line
 * based on the ambulance type and the currently selected insurer.
 *
 * Rules:
 *  - PLUS_DEFAULT_PRICE_FOR_INSURED → always "Cena", never "od" (price is the same for everyone)
 *  - DEFAULT / PLUS_WITH, no insurer selected → "Cena" + showFromPrefix (cheapest-across-all price)
 *  - DEFAULT / PLUS_WITH, SA00 selected → "Cena", no "od"
 *  - DEFAULT / PLUS_WITH, any other insurer → "Manažment návštevy", no "od"
 */
function resolvePriceDisplayMeta(
	ambulancePriceListTableType: AMBULANCE_PRICE_LIST_TABLE_TYPE | null,
	selectedInsuranceCode: string | null
): { priceLabel: string; showFromPrefix: boolean } {
	if (
		ambulancePriceListTableType ===
		AMBULANCE_PRICE_LIST_TABLE_TYPE.PLUS_DEFAULT_PRICE_FOR_INSURED
	) {
		// Fixed price for everyone — always show as a plain "Cena" amount.
		return { priceLabel: 'Cena', showFromPrefix: false }
	}

	// DEFAULT / PLUS_WITH_AND_WITHOUT_YEAR_PROGRAM_PRICE
	if (!selectedInsuranceCode) {
		// No insurer selected yet: show the minimum possible price with "od" prefix.
		return { priceLabel: 'Cena', showFromPrefix: true }
	}

	if (isSelfPaidInsuranceCode(selectedInsuranceCode)) {
		// Self-paying patient: plain price, label "Cena".
		return { priceLabel: 'Cena', showFromPrefix: false }
	}

	// Any insured patient (public insurers, EU variants, etc.): "Manažment návštevy".
	return { priceLabel: 'Manažment návštevy', showFromPrefix: false }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Keeps `templateCartPricing` in the order-flow context in sync with the
 * currently selected insurer and any annual-program discount, so the cart
 * sidebar, mobile header, and order summary always show the correct amount.
 *
 * Price-computation strategy by ambulance type:
 *
 * PLUS_DEFAULT_PRICE_FOR_INSURED
 *   The price is identical for every insurer — always use the SA00 (self-payer)
 *   entry as the canonical value. Insurance selection does not change the price.
 *
 * DEFAULT | PLUS_WITH_AND_WITHOUT_YEAR_PROGRAM_PRICE
 *   - No insurer selected: display the cheapest price across ALL insurance companies
 *     with an "od" prefix so the patient knows this is a minimum estimate.
 *   - Insurer selected: use the exact insurer row price (+ annual-program rules).
 *     SA00 → label "Cena"; any other → label "Manažment návštevy".
 *
 * The snapshot is cleared on unmount so the cart never shows a stale price
 * from a previous step-3 visit.
 */
export function useTemplateCartPricingSync({
	primaryProduct,
	primaryType,
	timeslotDetail,
	showInsurance,
	watchedInsurance,
	annualProgramDiscount
}: UseTemplateCartPricingSyncParams) {
	const { setTemplateCartPricing, ambulancePriceListTableType } = useOrderFlow()

	// Clear snapshot when leaving step 3.
	useEffect(() => {
		return () => setTemplateCartPricing(null)
	}, [setTemplateCartPricing])

	useEffect(() => {
		if (primaryType !== CartProductType.TEMPLATE || !primaryProduct) {
			setTemplateCartPricing(null)
			return
		}
		if (primaryProduct.type !== CartProductType.TEMPLATE) {
			setTemplateCartPricing(null)
			return
		}

		const tpl = timeslotDetail?.data?.template
		if (!tpl) {
			setTemplateCartPricing(null)
			return
		}

		const tsId = primaryProduct.data.timeslotId

		// ------------------------------------------------------------------
		// PLUS_DEFAULT_PRICE_FOR_INSURED — fixed self-payer price for all
		// ------------------------------------------------------------------
		if (
			ambulancePriceListTableType ===
			AMBULANCE_PRICE_LIST_TABLE_TYPE.PLUS_DEFAULT_PRICE_FOR_INSURED
		) {
			const fixedPrice =
				getSelfPaidInsurancePrice(tpl.insuranceCompanies, tpl.price) ?? tpl.price ?? 0

			setTemplateCartPricing((prev: TemplateCartPricingSnapshot | null) => {
				if (
					prev &&
					prev.timeslotId === tsId &&
					prev.referencePrice === fixedPrice &&
					prev.finalPrice === fixedPrice &&
					prev.priceLabel === 'Cena' &&
					!prev.showFromPrefix
				) {
					return prev
				}
				return {
					timeslotId: tsId,
					referencePrice: fixedPrice,
					finalPrice: fixedPrice,
					priceLabel: 'Cena',
					showFromPrefix: false
				}
			})
			return
		}

		// ------------------------------------------------------------------
		// DEFAULT / PLUS_WITH — price depends on selected insurer
		// ------------------------------------------------------------------

		const rawInsuranceId = showInsurance ? (watchedInsurance ?? null) : null

		if (!rawInsuranceId) {
			// No insurer selected yet: show the global minimum with "od" prefix.
			const minPrice =
				getCheapestAllInsurancePrice(tpl.insuranceCompanies, tpl.price) ?? tpl.price ?? 0

			setTemplateCartPricing((prev: TemplateCartPricingSnapshot | null) => {
				if (
					prev &&
					prev.timeslotId === tsId &&
					prev.referencePrice === minPrice &&
					prev.finalPrice === minPrice &&
					prev.priceLabel === 'Cena' &&
					prev.showFromPrefix
				) {
					return prev
				}
				return {
					timeslotId: tsId,
					referencePrice: minPrice,
					finalPrice: minPrice,
					priceLabel: 'Cena',
					showFromPrefix: true
				}
			})
			return
		}

		// Insurer is selected — resolve exact price (with annual-program rules).
		const selectedInsuranceCode = findInsuranceCodeById(tpl.insuranceCompanies, rawInsuranceId)
		const { priceLabel, showFromPrefix } = resolvePriceDisplayMeta(
			ambulancePriceListTableType,
			selectedInsuranceCode
		)

		const referencePrice =
			getTemplatePriceBeforeAnnualProgram(timeslotDetail, rawInsuranceId) ?? tpl.price ?? 0
		const finalPrice =
			calculateDoctorAmbulancePrice(timeslotDetail, rawInsuranceId, annualProgramDiscount)
				.price ?? referencePrice

		setTemplateCartPricing((prev: TemplateCartPricingSnapshot | null) => {
			if (
				prev &&
				prev.timeslotId === tsId &&
				prev.referencePrice === referencePrice &&
				prev.finalPrice === finalPrice &&
				prev.priceLabel === priceLabel &&
				prev.showFromPrefix === showFromPrefix
			) {
				return prev
			}
			return { timeslotId: tsId, referencePrice, finalPrice, priceLabel, showFromPrefix }
		})
	}, [
		ambulancePriceListTableType,
		annualProgramDiscount,
		primaryProduct,
		primaryType,
		setTemplateCartPricing,
		showInsurance,
		timeslotDetail,
		watchedInsurance
	])
}
